import h from 'hyperscript';

import { saveFeature } from 'cadenza/api-client/maps-api';
import { LayerProperty } from 'cadenza/map/layer-property';
import { MapProperty } from 'cadenza/map/map-property';
import {
  AbstractFeaturePanel,
  NOTIFICATION_TYPE
} from 'cadenza/map/feature-attributes-panel/abstract-feature-panel';
import { refreshLayer } from 'cadenza/map/layer-utils';
import { logger } from 'cadenza/utils/logging';
import { serializeLayerGeometries } from 'cadenza/map/create-new-feature-panel/create-new-feature-panel';
import { context } from 'cadenza/map/context/context';
import { BlockIdentifier, getBlockSettingsSingleton } from 'cadenza/map/block-settings';

import i18n from './messages.properties';

const NAME = 'd-edit-geometry-panel';

export class EditGeometryPanel extends AbstractFeaturePanel {

  #drawLayerSource;
  #geometryType;
  #snappingLabel;

  constructor ({ title = i18n('title'), drawLayerSource, geometryType, snappingLabel, ...otherOptions } = {}) {
    super({ title, ...otherOptions });
    this.#drawLayerSource = drawLayerSource;
    this.#geometryType = geometryType;
    this.#snappingLabel = snappingLabel;
  }

  connectedCallback () {
    super.connectedCallback();
    this.classList.add(NAME);
    this.closest('.map-and-sidebar').classList.add('column-sm');
    if (getBlockSettingsSingleton().isBlockVisible(BlockIdentifier.SNAPPING_CONTROL, true)) {
      this.querySelector('.content')
        .append(createSnappingControl(this.map, this.#snappingLabel));
    }
  }

  disconnectedCallback () {
    super.disconnectedCallback();
    refreshLayer(this.selectedLayer);
  }

  onMapPrimaryInteractionChange () {
    this.saveGeometry().catch(error => logger.error('Could not save geometry', error));
    this.close(false);
  }

  /**
   * Saves the geometry being edited. This can be overwritten by subclasses to save the geometry in different ways.
   *
   * @protected
   * @return a promise resolving after the feature has been saved and the underlying layer refreshed
   */
  saveGeometry () {
    const layerId = this.selectedLayer.get(LayerProperty.ID);
    const featureId = this.selectedLayer.get(LayerProperty.SELECTED_FEATURE_IDS)[0];
    return saveFeature(layerId, featureId, this._getFeatureDtoWithGeometryOnly(featureId))
      .then(() => {
        refreshLayer(this.selectedLayer);
      });
  }

  _getFeatureDtoWithGeometryOnly (featureId) {
    const geometriesJSON = serializeLayerGeometries(this.#drawLayerSource, this.#geometryType);
    return { featureId, geometry: geometriesJSON };
  }

  onSave (shouldResetInteraction) {
    this.saveGeometry().then(() => {
      super.onSave(shouldResetInteraction);
    }).catch(errorResponse => this.showErrorsInNotificationArea(
      this._getErrorMessages(errorResponse?.content?.validationMessages))
    );
  }

  _getErrorMessages (messages = []) {
    return messages.map(notification => {
      switch (notification.type) {
        case NOTIFICATION_TYPE.VALIDATOR :
          return notification.message;
        case NOTIFICATION_TYPE.GEOMETRY :
          logger.error('Error while saving geometry', notification.message);
          return i18n('geometryError');
        default:
          throw new Error(`Error ${JSON.stringify(notification)} not supported`);
      }
    });
  }

}

export function createSnappingControl (map, snappingLabel) {
  const label = snappingLabel || i18n('featureEditor.snapping');
  return h('form-group',
    [
      h('label', label),
      createSnappingSelect(map)
    ]
  );
}

function createSnappingSelect (map) {
  const onchange = (layerID) => {
    const primaryInteraction = map.get(MapProperty.PRIMARY_INTERACTION);
    if (primaryInteraction) {
      primaryInteraction.snappingLayerId = layerID;
    }
  };
  const options = context(map).layers.filter(l => l.get(LayerProperty.SNAPPABLE)).map(l => {
    const layerId = l.get(LayerProperty.ID);
    const layerName = l.get(LayerProperty.NAME);
    return createSnappingSelectOption(layerName, layerId);
  });
  options.unshift(createSnappingSelectOption(i18n('featureEditor.noLayer'), ''));
  return h('select', {
    onchange: event => onchange(event.currentTarget.value)
  }, options);
}

function createSnappingSelectOption (text, value) {
  return h('option', { text, value });
}

customElements.define(NAME, EditGeometryPanel);
